import { type ReactNode, useEffect, useRef } from "react"

import { useActionsRegistry } from "./actions-registry"

import { type WatchBinding } from "./catalog"

import { type DashboardStateStore, useDashboardStateStore } from "../state/dashboard-state"

import { resolvePropValue } from "../utils/expressions"

/**
 * Runtime that subscribes to dashboard state and dispatches actions
 * declared via `spec.watchBindings`. Companion to `queryBindings`:
 * queryBindings re-runs SQL when watched paths change; this dispatches
 * arbitrary registered actions (setState, drillTo, exportCsv, …).
 *
 * Fires on change only — never on the initial mount. The first
 * snapshot recorded for each path is the "baseline"; subsequent reads
 * fire only when the path's value differs from the baseline. This
 * matches `vercel-labs/json-render` and prevents a flurry of dispatches
 * on every render.
 */
export interface WatchRuntimeProps {
  bindings: WatchBinding[] | undefined
  children: ReactNode
}

export function WatchRuntime({ bindings, children }: WatchRuntimeProps) {
  const stateStore = useDashboardStateStore()
  const registry = useActionsRegistry()

  // Last seen value per state_path — keeps the no-fire-on-mount semantic.
  // We don't reset this when bindings change: the same path might still
  // be watched by a different binding and the prior baseline is still
  // valid. Cleared only on unmount.
  const lastSeenRef = useRef<Map<string, unknown>>(new Map())

  // Track baseline initialisation so the first subscribe-callback fire
  // populates lastSeen but does not dispatch.
  const initialisedRef = useRef(false)

  useEffect(() => {
    initialisedRef.current = false
    return () => {
      lastSeenRef.current.clear()
    }
    // bindings list change is not a reason to reset baseline — see
    // comment on lastSeenRef. Only re-run when stateStore identity
    // flips (provider remount).
  }, [stateStore])

  useEffect(() => {
    if (!bindings || bindings.length === 0) return undefined

    const evaluate = () => {
      const snapshot = stateStore.getSnapshot()
      const isInitial = !initialisedRef.current
      initialisedRef.current = true

      // Read every watched path once per change — even if multiple
      // bindings reference the same state_path, the resolution is
      // memoised by the snapshot identity comparison below.
      const paths = new Set<string>()
      for (const b of bindings) paths.add(b.state_path)

      const changedPaths = new Set<string>()
      for (const path of paths) {
        const next = readJsonPointer(snapshot, path)
        const prev = lastSeenRef.current.get(path)
        const seen = lastSeenRef.current.has(path)
        if (!seen || !shallowEquals(prev, next)) {
          lastSeenRef.current.set(path, next)
          if (seen && !isInitial) changedPaths.add(path)
        }
      }

      if (isInitial || changedPaths.size === 0) return

      // Dispatch only for bindings whose state_path is in changedPaths.
      // A given state_path may have multiple bindings — fire all of
      // them, in array order, with no awaiting between dispatches so
      // ordering is preserved but a slow handler doesn't block siblings.
      for (const binding of bindings) {
        if (!changedPaths.has(binding.state_path)) continue
        if (!registry) {
          // No registry mounted — surface in dev so the missing
          // ActionsRegistryProvider doesn't fail silently. Skipped in
          // prod to avoid console noise on misconfigured embeds.
          if (process.env.NODE_ENV !== "production") {
            console.error(
              "[dash] watchBindings present but no ActionsRegistryProvider — action %o on %o will not fire",
              binding.action,
              binding.state_path
            )
          }
          continue
        }
        const resolvedParams = resolveWatchParams(binding.params, stateStore)
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        registry.dispatch(binding.action, resolvedParams).then((result) => {
          if (!result.ok) {
            // A typo'd action name or a thrown handler used to be a
            // silent no-op. Surface so devtools (§1.1) and the user
            // see why their watch did nothing.
            console.error(
              "[dash] watch dispatch failed: action=%o path=%o error=%o",
              binding.action,
              binding.state_path,
              result.error
            )
          }
        })
      }
    }

    // Subscribe BEFORE the baseline pass — a state mutation that
    // lands in the gap between baseline and subscribe would otherwise
    // be observed by neither (its old value is in lastSeenRef as the
    // baseline; its new value is already current by the time
    // subscribe fires). Subscribing first means the baseline pass
    // still skips dispatch via `isInitial`, but any changes that
    // arrive during the same microtask are caught.
    const unsub = stateStore.subscribe(evaluate)
    evaluate()
    return unsub
  }, [bindings, registry, stateStore])

  return <>{children}</>
}

// ── helpers ─────────────────────────────────────────────────────────

function resolveWatchParams(
  params: Record<string, unknown> | undefined,
  stateStore: DashboardStateStore
): Record<string, unknown> | undefined {
  if (!params) return undefined
  const ctx = { getState: (path: string) => stateStore.getDashState(path) }
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(params)) {
    out[k] = resolvePropValue(v, ctx)
  }
  return out
}

/**
 * Read a JSON-Pointer path from a snapshot. Mirrors the semantics of
 * `DashboardStateStore.getDashState` but operates on a captured
 * snapshot — used so we can compare the current value against
 * `lastSeen` without triggering another `subscribe`.
 *
 * **Deviation from RFC 6901**: arrays are treated as leaf values, not
 * intermediate containers. A path like `/items/0/value` returns
 * `undefined` instead of indexing into the array. This matches the
 * dashboard state store, which only supports object-keyed paths;
 * watching individual array elements via numeric indices isn't a
 * supported binding shape. Authors who need per-element observability
 * should restructure state as a keyed object (`{by_id: {...}}`)
 * rather than a list. The escape sequences `~0` and `~1` for `~`/`/`
 * in keys ARE supported per RFC 6901.
 */
export function readJsonPointer(snapshot: Record<string, unknown>, path: string): unknown {
  if (!path || path === "/") return snapshot
  const segments = path
    .replace(/^\//, "")
    .split("/")
    .map((seg) => seg.replace(/~1/g, "/").replace(/~0/g, "~"))
  let current: unknown = snapshot
  for (const seg of segments) {
    if (current && typeof current === "object" && !Array.isArray(current)) {
      current = (current as Record<string, unknown>)[seg]
    } else {
      return undefined
    }
  }
  return current
}

/**
 * Shallow equality with reference-identity short-circuit. Adequate for
 * watch path comparison because the state store uses immutable updates
 * (a changed branch produces a new reference), so reference equality
 * already catches most no-ops; the type/value fall-through handles
 * leaf primitives.
 */
function shallowEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (a === null || b === null) return a === b
  // Different references for objects/arrays count as a change — that's
  // the desired semantic: any structural mutation triggers a fire.
  return false
}
